import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import LocationAdminForm from "Components/Admin/LocationAdminForm";
import { useAuth } from "context/AuthProvider";
import { updateLocation, discardLocation } from "services/locationService";

const FLAGS = [
  { key: "usesRankings", label: "Classements" },
  { key: "displayRanking", label: "Classement affiché" },
  { key: "allowMyMatches", label: "Mes matchs" },
  { key: "allowCrossPool", label: "Pools croisés" },
  { key: "testEnv", label: "Test" },
];

const filterButton = (active) =>
  `px-4 py-2 rounded ${active ? "bg-amber-600" : "bg-gray-700"} hover:bg-amber-500 transition`;

/**
 * Master-only list of every location (active or not) with create, edit and delete.
 * Deleting only discards (flag + date): discarded locations leave this table but stay in
 * the database. `locations` includes them, so the form keeps their ids taken.
 * `locations` and `users` are owned by the dashboard: admins are assigned from the users tab,
 * and are listed here per location.
 */
const LocationManager = ({ locations, refreshLocations, users = [] }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [formLocation, setFormLocation] = useState(undefined); // undefined = closed, null = create
  const [busyId, setBusyId] = useState(null);

  const adminsByLocation = useMemo(() => {
    const map = {};
    users.forEach((u) =>
      (u.adminLocations || []).forEach((id) => (map[id] = [...(map[id] || []), u]))
    );
    return map;
  }, [users]);

  const listed = useMemo(() => locations.filter((l) => !l.discarded), [locations]);

  const filtered = useMemo(() => {
    const lower = searchQuery.trim().toLowerCase();
    return listed
      .filter((l) => (filter === "active" ? l.active : filter === "inactive" ? !l.active : true))
      .filter(
        (l) =>
          !lower ||
          [
            l.name,
            l.table,
            l.town,
            l.id,
            ...(adminsByLocation[l.id] || []).flatMap((u) => [u.name, u.email]),
          ].some((v) => v?.toLowerCase().includes(lower))
      )
      .sort(
        (a, b) =>
          Number(!!b.active) - Number(!!a.active) ||
          `${a.name} ${a.table || ""}`.localeCompare(`${b.name} ${b.table || ""}`, "fr")
      );
  }, [listed, searchQuery, filter, adminsByLocation]);

  const activeCount = listed.filter((l) => l.active).length;

  const handleToggleActive = async (location, active) => {
    setBusyId(location.id);
    try {
      await updateLocation(location.id, { active });
      await refreshLocations();
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error toggling location:", err);
      window.alert("Erreur lors de la mise à jour.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (location) => {
    const label = `${location.name}${location.table ? ` (${location.table})` : ""}`;
    if (
      !window.confirm(
        `Supprimer « ${label} » ?\n\n` +
          "Le lieu disparaîtra du site et de cette liste, mais ses données (tournois, classements, admins) sont conservées."
      )
    )
      return;
    setBusyId(location.id);
    try {
      await discardLocation(location.id, user?.uid);
      await refreshLocations();
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error deleting location:", err);
      window.alert("Erreur lors de la suppression.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSaved = async () => {
    setFormLocation(undefined);
    await refreshLocations();
  };

  return (
    <div className="space-y-4">
      {/* Filters + Search + Add */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <input
          type="text"
          placeholder="Rechercher par nom, table, ville, identifiant ou admin"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-4 py-2 rounded-md w-full md:w-1/2 bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} className={filterButton(filter === "all")}>
            Tous ({listed.length})
          </button>
          <button onClick={() => setFilter("active")} className={filterButton(filter === "active")}>
            Actifs ({activeCount})
          </button>
          <button
            onClick={() => setFilter("inactive")}
            className={filterButton(filter === "inactive")}
          >
            Inactifs ({listed.length - activeCount})
          </button>
          <button
            onClick={() => setFormLocation(null)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-green-600 hover:bg-green-700 font-semibold transition"
          >
            <Plus size={16} /> Ajouter un lieu
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-600 text-amber-300">
              <th className="p-2">Lieu</th>
              <th className="p-2">Ville</th>
              <th className="p-2">Admins</th>
              <th className="p-2">Options</th>
              <th className="p-2 text-center">Actif</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((loc) => (
              <tr
                key={loc.id}
                className={`border-b border-gray-600 align-top ${loc.active ? "" : "text-gray-400"}`}
              >
                <td className="p-2">
                  <div className="flex items-center gap-3">
                    {loc.image ? (
                      <img
                        src={loc.image}
                        alt=""
                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gray-700 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold">{loc.name || <i>Sans nom</i>}</div>
                      {loc.table && <div className="text-sm text-gray-400">{loc.table}</div>}
                      <div className="text-xs text-gray-500 font-mono">{loc.id}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2">{loc.town || <i>—</i>}</td>
                <td className="p-2 text-sm">
                  {(adminsByLocation[loc.id] || []).length > 0 ? (
                    <ul className="space-y-0.5">
                      {adminsByLocation[loc.id].map((u) => (
                        <li key={u.id} title={u.email}>
                          {u.name || u.email}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <i className="text-gray-500">Aucun</i>
                  )}
                </td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {FLAGS.filter((f) => loc[f.key]).map((f) => (
                      <span
                        key={f.key}
                        className="px-2 py-0.5 rounded-full bg-gray-700 text-xs text-gray-200 whitespace-nowrap"
                      >
                        {f.label}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!loc.active}
                    disabled={busyId === loc.id}
                    onChange={(e) => handleToggleActive(loc, e.target.checked)}
                    title={loc.active ? "Désactiver" : "Activer"}
                  />
                </td>
                <td className="p-2">
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/locations/${loc.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold transition"
                      title="Voir la page du lieu"
                    >
                      <ExternalLink size={14} /> Voir
                    </Link>
                    <button
                      onClick={() => setFormLocation(loc)}
                      disabled={busyId === loc.id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition disabled:opacity-50"
                    >
                      <Pencil size={14} /> Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(loc)}
                      disabled={busyId === loc.id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition disabled:opacity-50"
                    >
                      <Trash2 size={14} /> Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 py-8">Aucun lieu ne correspond à la recherche.</p>
        )}
      </div>

      {formLocation !== undefined && (
        <LocationAdminForm
          location={formLocation}
          locations={locations}
          onClose={() => setFormLocation(undefined)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default LocationManager;
